import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "pivot_cookie_consent_v1";

/** Cookie consent banner — GDPR/CCPA compliant. Fires GA4 only after Accept. */
const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) setVisible(true);
  }, []);

  const setConsent = (level) => {
    // level: 'all' | 'essential'
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ level, decided_at: new Date().toISOString() })
    );
    // Update gtag consent state (GA4)
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: level === "all" ? "granted" : "denied",
        ad_storage: level === "all" ? "granted" : "denied",
        ad_user_data: level === "all" ? "granted" : "denied",
        ad_personalization: level === "all" ? "granted" : "denied",
      });
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:bottom-4 md:max-w-md z-[9998] bg-zinc-950 border-2 border-primary/40 shadow-2xl rounded-sm p-5"
          data-testid="cookie-consent-banner"
          role="dialog"
          aria-label="Cookie consent"
        >
          <div className="flex items-start gap-3 mb-3">
            <Cookie className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-heading font-bold text-white text-sm uppercase tracking-wider mb-1">
                We Use Cookies
              </h3>
              <p className="text-xs text-zinc-400 leading-relaxed">
                We use essential cookies to run the app, and analytics cookies to understand
                how athletes use Pivot Performance. Your choice — pick below.
              </p>
            </div>
            <button
              onClick={() => setConsent("essential")}
              className="text-zinc-500 hover:text-white transition-colors"
              aria-label="Close"
              data-testid="cookie-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {showDetails && (
            <div className="text-xs text-zinc-500 mb-3 p-3 bg-zinc-900 rounded-sm space-y-2">
              <div>
                <strong className="text-zinc-300">Essential</strong> — login session, security,
                preferences. Always on.
              </div>
              <div>
                <strong className="text-zinc-300">Analytics (Google Analytics 4)</strong> — page
                views, feature usage, anonymous device info. Helps us improve the app.
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={() => setConsent("all")}
              className="flex-1 bg-primary hover:bg-primary/90 font-bold uppercase text-xs h-9"
              data-testid="cookie-accept-all-btn"
            >
              Accept All
            </Button>
            <Button
              onClick={() => setConsent("essential")}
              variant="outline"
              className="flex-1 font-bold uppercase text-xs h-9 border-zinc-700 hover:bg-zinc-800"
              data-testid="cookie-essential-btn"
            >
              Essential Only
            </Button>
            <Button
              onClick={() => setShowDetails((v) => !v)}
              variant="ghost"
              className="font-bold uppercase text-xs h-9 text-zinc-400 hover:text-white"
              data-testid="cookie-details-btn"
            >
              {showDetails ? "Hide" : "Details"}
            </Button>
          </div>

          <p className="text-[10px] text-zinc-600 mt-3 leading-relaxed">
            See our{" "}
            <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>{" "}
            for details. You can change this anytime in your profile settings.
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
